import type { Item, ItemDraft } from '@easymoving/types';
import { CloudBackend } from './cloud';
import { LocalBackend } from './local';

/**
 * Build the cloud ItemDraft for a local item. room/category are stored as value
 * strings (carry over as-is); boxId is an id, so it's remapped local→cloud.
 * Pure → unit tested.
 */
export function buildMigratedItemDraft(item: Item, boxIdMap: Record<string, string>): ItemDraft {
  return {
    name: item.name,
    nickname: item.nickname,
    room: item.room,
    category: item.category,
    iconType: item.iconType,
    destination: item.destination,
    progressStatus: item.progressStatus,
    length: item.length,
    width: item.width,
    height: item.height,
    dimensionUnit: item.dimensionUnit,
    dimensionsText: item.dimensionsText,
    weight: item.weight,
    condition: item.condition,
    quantity: item.quantity,
    estimatedValue: item.estimatedValue,
    currency: item.currency,
    boxId: item.boxId ? boxIdMap[item.boxId] : undefined,
    storageLocation: item.storageLocation,
    fragile: item.fragile,
    priority: item.priority,
    notes: item.notes,
  };
}

export interface MigrationResult {
  items: number;
  boxes: number;
}

/**
 * Copy all on-device guest data to the signed-in user's cloud account, then
 * clear the local store. Order: rooms/categories (by name) → boxes (id remap)
 * → items (remapped boxId + photo upload).
 */
export async function migrateLocalToCloud(
  uid: string,
  onProgress?: (done: number, total: number) => void,
): Promise<MigrationResult> {
  const local = new LocalBackend();
  const data = await local.exportForMigration();
  const total = data.items.length;
  if (
    total === 0 &&
    data.boxes.length === 0 &&
    data.rooms.length === 0 &&
    data.categories.length === 0
  ) {
    return { items: 0, boxes: 0 };
  }

  const cloud = new CloudBackend(uid);

  for (const r of data.rooms) await cloud.createCustomRoom(r.name);
  for (const c of data.categories) await cloud.createCustomCategory(c.name);

  const boxIdMap: Record<string, string> = {};
  for (const b of data.boxes) {
    boxIdMap[b.id] = await cloud.createBox({
      name: b.name,
      room: b.room,
      destination: b.destination,
      progressStatus: b.progressStatus,
      storageLocation: b.storageLocation,
      notes: b.notes,
    });
  }

  let done = 0;
  onProgress?.(0, total);
  for (const it of data.items) {
    const newId = await cloud.createItem(buildMigratedItemDraft(it, boxIdMap));
    const primary = it.images?.[0];
    if (primary?.storagePath) {
      const blob = data.blobs.get(primary.storagePath);
      if (blob) {
        const file = new File([blob], 'original.jpg', { type: blob.type || 'image/jpeg' });
        await cloud.uploadPrimaryImage(newId, file);
      }
    }
    done += 1;
    onProgress?.(done, total);
  }

  if (data.profile) {
    const { updatedAt: _u, ...draft } = data.profile;
    await cloud.saveMoveProfile(draft);
  }

  await local.clearAll();
  return { items: total, boxes: data.boxes.length };
}
